import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshPhysicalNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Subsurface-style thickness shading on `MeshPhysicalNodeMaterial` for wax/skin-like surfaces. */
@EngineClass('SSSNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'SSS (Subsurface Scattering)',
  nodeMaterialGroup: 'TSL Node FX',
})
export class SSSNodeMaterialAsset extends NodeMaterialAsset(MeshPhysicalNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Base material color' })
  override color = new THREE.Color(1.0, 0.2, 0.2);

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.3;

  @property({ type: 'number', min: 0.01, max: 1.0, step: 0.01, description: 'Thickness distortion' })
    thicknessDistortion = 0.1;

  @property({ type: 'number', min: 0.01, max: 5.0, step: 0.05, description: 'Thickness ambient' })
    thicknessAmbient = 0.4;

  @property({ type: 'number', min: 0.01, max: 5.0, step: 0.05, description: 'Thickness attenuation' })
    thicknessAttenuation = 0.8;

  @property({ type: 'number', min: 0.01, max: 16.0, step: 0.1, description: 'Thickness power' })
    thicknessPower = 2.0;

  @property({ type: 'number', min: 0.01, max: 50.0, step: 0.1, description: 'Thickness scale' })
    thicknessScale = 16.0;

  private readonly _roughnessUniform = TSL.uniform(this.roughness);
  private readonly _thicknessDistortionUniform = TSL.uniform(this.thicknessDistortion);
  private readonly _thicknessAmbientUniform = TSL.uniform(this.thicknessAmbient);
  private readonly _thicknessAttenuationUniform = TSL.uniform(this.thicknessAttenuation);
  private readonly _thicknessPowerUniform = TSL.uniform(this.thicknessPower);
  private readonly _thicknessScaleUniform = TSL.uniform(this.thicknessScale);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.colorNode = null;

    this._roughnessUniform.value = this.roughness;
    this._thicknessDistortionUniform.value = this.thicknessDistortion;
    this._thicknessAmbientUniform.value = this.thicknessAmbient;
    this._thicknessAttenuationUniform.value = this.thicknessAttenuation;
    this._thicknessPowerUniform.value = this.thicknessPower;
    this._thicknessScaleUniform.value = this.thicknessScale;

    this.roughnessNode = this._roughnessUniform;
    (this as any).thicknessColorNode = TSL.color(this.color);
    (this as any).thicknessDistortionNode = this._thicknessDistortionUniform;
    (this as any).thicknessAmbientNode = this._thicknessAmbientUniform;
    (this as any).thicknessAttenuationNode = this._thicknessAttenuationUniform;
    (this as any).thicknessPowerNode = this._thicknessPowerUniform;
    (this as any).thicknessScaleNode = this._thicknessScaleUniform;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): SSSNodeMaterialAsset {
    const instance = new SSSNodeMaterialAsset();
    SSSNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
